import { useQueryClient } from '@tanstack/react-query'
import { invalidateTypedQueries, useApiClient, type InferRequest } from '../../hooks/useApiClient'
import { useMatchCacheUtils } from './cacheUtils'

export type MatchLinkRequest = InferRequest<'/supplier_rec/match/link'>
export type MatchOptionsForInvoiceRequest = InferRequest<'/supplier_rec/match/options_for_invoice'>
export type MatchOptionsForBolRequest = InferRequest<'/supplier_rec/match/options_for_bol'>

export function useMatchLinkingActions() {
  const queryClient = useQueryClient()
  const $api = useApiClient()
  const cacheUtils = useMatchCacheUtils(queryClient)

  const useLinkMatch = () => {
    return $api.useMutation('post', '/supplier_rec/match/link', {
      onMutate: async (request) => {
        await cacheUtils.cancelMatchQueries()

        const previousData = cacheUtils.getPreviousQueryData()

        return { previousData, request }
      },
      onSuccess: (response, _, __) => {
        const cacheUpdate = cacheUtils.processApiResponse(response, 'link')
        cacheUtils.updateMatchOverviewCache(cacheUpdate)
        invalidateTypedQueries(queryClient, 'post', '/supplier_rec/match/detail')
      },
      onError: (_, __, context: { previousData?: [unknown, unknown][] } | undefined) => {
        if (context?.previousData) {
          cacheUtils.rollbackQueryData(context.previousData)
        }
      },
    })
  }

  const useMatchOptionsForInvoice = (params: {
    invoice_id: string
    match_id?: string
    search_text?: string
    min_volume?: number
    max_volume?: number
    include_matched?: boolean
  }) => {
    return $api.useQuery(
      'post',
      '/supplier_rec/match/options_for_invoice',
      {
        params: {
          query: params,
        },
      },
      {
        enabled: !!params.invoice_id,
      }
    )
  }

  const useMatchOptionsForBol = (params: {
    bol_id: string
    match_id?: string
    search_text?: string
    min_volume?: number
    max_volume?: number
    include_matched?: boolean
  }) => {
    return $api.useQuery(
      'post',
      '/supplier_rec/match/options_for_bol',
      {
        params: {
          query: params,
        },
      },
      {
        enabled: !!params.bol_id,
      }
    )
  }

  return {
    useLinkMatch,
    useMatchOptionsForInvoice,
    useMatchOptionsForBol,
  }
}
